import { useEffect, useMemo, useState } from 'react';
import Button from 'devextreme-react/button';
import LoadIndicator from 'devextreme-react/load-indicator';
import { FilePreviewPopupShell } from '../mediaViewer/FilePreviewPopupShell';
import { useImagePanZoom } from '../mediaViewer/useImagePanZoom';
import '../mediaViewer/MediaPreviewPopup.css';
import '../mediaViewer/mediaViewerStage.css';
import { mermaidDiagramTitle, resolveMermaidTheme } from './markdownPreviewRender';
import { renderMermaidSvg } from './mermaidRenderer';
import './markdownPreview.css';

export type MermaidPreviewPopupProps = {
  visible: boolean;
  source: string | null;
  onClose: () => void;
};

export function MermaidPreviewPopup({ visible, source, onClose }: MermaidPreviewPopupProps) {
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [themeTick, setThemeTick] = useState(0);
  const panZoom = useImagePanZoom(visible && Boolean(svg));
  const { reset: resetPanZoom } = panZoom;

  const title = useMemo(
    () => (source ? mermaidDiagramTitle(source) : 'Mermaid diagram'),
    [source]
  );

  useEffect(() => {
    if (!visible || typeof document === 'undefined') return;
    const root = document.documentElement;
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((m) => m.attributeName === 'data-theme')) {
        setThemeTick((value) => value + 1);
      }
    });
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible || !source?.trim()) {
      setSvg(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setSvg(null);
    resetPanZoom();

    void (async () => {
      try {
        const next = await renderMermaidSvg(source, resolveMermaidTheme());
        if (cancelled) return;
        setSvg(next);
        setLoading(false);
        resetPanZoom();
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not render Mermaid diagram.');
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [visible, source, themeTick, resetPanZoom]);

  return (
    <FilePreviewPopupShell visible={visible} title={title} onClose={onClose}>
      <div className="media-preview-popup__body mermaid-preview-popup__body">
        <div className="mermaid-preview-popup__toolbar" role="toolbar" aria-label="Diagram zoom">
          <Button
            icon="minus"
            stylingMode="text"
            hint="Zoom out"
            disabled={!svg}
            onClick={panZoom.zoomOut}
          />
          <Button
            icon="plus"
            stylingMode="text"
            hint="Zoom in"
            disabled={!svg}
            onClick={panZoom.zoomIn}
          />
          <Button
            icon="fullscreen"
            stylingMode="text"
            hint="Fit to view"
            disabled={!svg}
            onClick={panZoom.zoomFit}
          />
        </div>
        <div className="media-viewer-stage">
          {loading ? (
            <div className="media-viewer-stage__loading" aria-busy="true" aria-live="polite">
              <LoadIndicator visible height={28} width={28} />
              <span>Rendering diagram…</span>
            </div>
          ) : null}
          {error ? <p className="media-viewer-stage__error">{error}</p> : null}
          {!loading && !error && svg ? (
            <div
              className="media-viewer-stage__fit media-viewer-stage__fit--pannable"
              onWheel={panZoom.onWheel}
              onPointerDown={panZoom.onPointerDown}
              onPointerMove={panZoom.onPointerMove}
              onPointerUp={panZoom.onPointerUp}
              onPointerCancel={panZoom.onPointerUp}
            >
              <div
                className="mermaid-preview-popup__svg"
                style={{
                  transform: panZoom.transform,
                  cursor: panZoom.scale > 1 ? 'grab' : 'default'
                }}
                dangerouslySetInnerHTML={{ __html: svg }}
              />
            </div>
          ) : null}
        </div>
      </div>
    </FilePreviewPopupShell>
  );
}
